import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Box, Separator } from '@base-app';
import { PATHS } from '@constants/routes';

import { EMAIL, FOOTER_URL } from './Footer.types';
import {
  StyledAllRight,
  StyledClinicalLaboratory,
  StyledColumn,
  StyledContact,
  StyledContactContainer,
  StyledCopyright,
  StyledFooterContainer,
  StyledFooterLogo,
  StyledLink,
  StyledRow,
} from './Footer.styles';
import { COMMON_TRANSLATION } from '@constants/index';
import TermsOfServiceModal from '../TermsOfServiceModal/TermsOfServiceModal';

const Footer: FC = () => {
  const location = useLocation();
  const { t } = useTranslation([COMMON_TRANSLATION]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const isStudyInsights = location?.pathname === `/${PATHS.STUDY_INSIGHTS}`;
  const currentYear = new Date().getFullYear().toString();

  const handleToggleModal = (): void => {
    setIsModalOpen(!isModalOpen);
  };

  const footerLinks = [
    {
      label: `${t('footer.privacy')}`,
      url: FOOTER_URL.PRIVACY,
    },
    {
      label: `${t('footer.terms')}`,
      url: FOOTER_URL.TERMS,
      onClick: handleToggleModal,
    },
    {
      label: `${t('footer.notice')}`,
      url: FOOTER_URL.NOTICE,
    },
  ];

  return (
    <StyledFooterContainer css={{ marginLeft: isStudyInsights ? '$studyInsights$sidebarWidthClose' : 0 }}>
      <StyledColumn>
        <StyledRow>
          <StyledFooterLogo />
          <Box>
            {footerLinks.map(({ label, url, onClick }, index: number) => {
              return url !== '#' ? (
                <StyledLink key={index} target={'_blank'} href={url}>
                  {label}
                </StyledLink>
              ) : (
                <StyledLink key={index} onClick={onClick}>
                  {label}
                </StyledLink>
              );
            })}
          </Box>
        </StyledRow>
        <Separator css={{ margin: '$10 0' }} />
        <StyledRow
          css={{
            alignItems: 'flex-start',
            lineHeight: '$4',
            paddingBottom: '$10',
          }}
        >
          <Box>
            <StyledCopyright>{t('footer.copyright', { currentYear })}</StyledCopyright>
            <StyledAllRight>{t('footer.all_rights_reserved')}</StyledAllRight>
            <StyledAllRight>
              {t('footer.patent_text')}
              <StyledLink size="sm" target={'_blank'} href={t('footer.patent_url')}>
                {t('footer.patent_url')}
              </StyledLink>
            </StyledAllRight>
          </Box>
          <StyledContactContainer>
            <StyledContact>{t('footer.contact_us', { email: EMAIL })}</StyledContact>
            <StyledClinicalLaboratory>
              <Box>{t('footer.clia')}</Box>
              <Box>{t('footer.cap')}</Box>
            </StyledClinicalLaboratory>
          </StyledContactContainer>
        </StyledRow>
      </StyledColumn>
      {isModalOpen && <TermsOfServiceModal isOpen={isModalOpen} onClose={handleToggleModal} readOnly={true} />}
    </StyledFooterContainer>
  );
};

export default Footer;
